import { TemplateResult, html, LitElement } from 'lit';
import globalStyles from './styles/outline-star-rating.global.pcss?inline';
import encapsulatedStyles from './styles/outline-star-rating.encapsulated.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

export const starRaitingVariants = ['large', 'small'] as const;
export type StarRaitingVariant = (typeof starRaitingVariants)[number];

/**
 * The OutlineStarRating component
 * @element outline-star-rating
 */
@customElement('outline-star-rating')
export class OutlineStarRating extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  /**
   * String of the decimal rating out of five.
   */
  @property({ type: String, attribute: 'stars' })
  stars: string;

  /**
   * Number of reviews.
   */
  @property({ type: String, attribute: 'review-count' })
  reviewCount: string;

  /**
   * 'large' | 'small'
   */
  @property({ type: String, reflect: true })
  variant: StarRaitingVariant = 'large';

  render(): TemplateResult {
    const elements = this.generateStars();
    const starsRounded = Number(this.stars).toFixed(2);

    return html`
      <section class="star-rating">
        <div class="star-rating--stars">${elements}</div>
        <div class="star-rating--data">
          <div class="star-rating--rating">${starsRounded}</div>
          ${this.reviewCount ? html`<div class="star-rating--total">
                (${this.reviewCount} reviews)</div>` : html``}
        </div>
      </section>
    `;
  }

  generateStars() {
    const filled = Math.floor(+this.stars);
    const partial = Number(((Number(this.stars) % 1) * 100).toFixed(2));
    const empty = 5 - (filled + Math.ceil(Number(this.stars) % 1));
    const els = [];

    // Verify prop values
    if (!Number.isNaN(filled) && !Number.isNaN(partial) && filled < 6) {
      // Add number of filled stars
      els.push(this.starLoop('filled', filled));

      // Add partial star svg
      if (filled < 5 && partial > 0) {
        els.push(this.partialStarSVG(`${partial}%`));
        // Set css var for filled star percentage
        // handle remaining empty svgs        
        if (empty > 0) {
          els.push(this.starLoop('empty', empty));
        }
        // If no partial star
      } else if (empty > 0) {
        // handle remaining empty svgs
        els.push(this.starLoop('empty', empty));
      }
    }
    // Handle prop failure
    else {
      els.push(html`<span>No Current Rating</span>`);
    }
    return els;
  }

  starLoop(type: string, count: number) {
    const element = [];
    for (let i = 0; i < count; i++) {
      if (type === 'empty') {
        element.push(this.emptyStarSVG());
      } else {
        element.push(this.filledStarSVG());
      }
    }
    return element;
  }

  filledStarSVG() {
    return html`<div class="star">
      <svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 18 17" fill="none">
        <path
          d="M9.875.563A.962.962 0 0 0 9 0a.974.974 0 0 0-.906.563l-2 4.156-4.5.656a1.019 1.019 0 0 0-.813.688c-.094.343 0 .75.25 1l3.25 3.218-.75 4.563a.982.982 0 0 0 .406.969c.313.25.72.25 1.032.093L9 13.75l4 2.156c.344.156.75.156 1.063-.094a.982.982 0 0 0 .406-.968l-.781-4.563 3.25-3.219a.928.928 0 0 0 .25-1 1.019 1.019 0 0 0-.813-.687l-4.469-.656L9.875.562Z"          fill="#FB923C"
          stroke="#FB923C"
          stroke-width="1"
        ></path>
      </svg>
    </div>`;
  }

  emptyStarSVG() {
    return html`<div class="star">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 17" fill="none">
        <path
          d="M9.875.563A.962.962 0 0 0 9 0a.974.974 0 0 0-.906.563l-2 4.156-4.5.656a1.019 1.019 0 0 0-.813.688c-.094.343 0 .75.25 1l3.25 3.218-.75 4.563a.982.982 0 0 0 .406.969c.313.25.72.25 1.032.093L9 13.75l4 2.156c.344.156.75.156 1.063-.094a.982.982 0 0 0 .406-.968l-.781-4.563 3.25-3.219a.928.928 0 0 0 .25-1 1.019 1.019 0 0 0-.813-.687l-4.469-.656L9.875.562Z"          fill="transparent"
          stroke="#FB923C"
          stroke-width="1"
        ></path>
      </svg>
    </div> `;
  }

  partialStarSVG(percentToFill: string) {
    const svgString = `
    <div class="star">
      <svg xmlns="http://www.w3.org/2000/svg"  viewBox="0 0 18 17" fill="none">
          <defs>
            <linearGradient id="grad">
              <stop offset="0%" stop-color="#FB923C" />
              <stop offset="${percentToFill}" stop-color="#FB923C" />
              <stop offset="${percentToFill}" stop-color="transparent" />
              <stop offset="100%" stop-color="transparent" />
            </linearGradient>
          </defs>
          <path d="M9.875.563A.962.962 0 0 0 9 0a.974.974 0 0 0-.906.563l-2 4.156-4.5.656a1.019 1.019 0 0 0-.813.688c-.094.343 0 .75.25 1l3.25 3.218-.75 4.563a.982.982 0 0 0 .406.969c.313.25.72.25 1.032.093L9 13.75l4 2.156c.344.156.75.156 1.063-.094a.982.982 0 0 0 .406-.968l-.781-4.563 3.25-3.219a.928.928 0 0 0 .25-1 1.019 1.019 0 0 0-.813-.687l-4.469-.656L9.875.562Z" fill="url(#grad)" stroke="#FB923C" stroke-width="1" />
        </svg>
      </div>
    `;
    return unsafeHTML(svgString);
  }
}
